import type { QueryRowType } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries, roundedHoursAgo } from "../../../shared/queries";
import { config } from "../../environment";
import { useAdminSpaceView } from "../../hooks/useAdminSpaceView";
import { useAdminViewSubject } from "../../hooks/useAdminViewSubject";
import { useGetPresence } from "../../hooks/useGetPresence";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useQuery } from "../../hooks/useQuery";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { isSpaceMember, useSpaces } from "../../hooks/useSpaces";
import { useUrlSort } from "../../hooks/useUrlSort";
import { branchLastActivity } from "../../lib/branch";
import {
  connectorAvatarItems,
  mainBranchConnectorIds,
} from "../../lib/connector";
import {
  makeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import {
  type TriggerType,
  triggerTypeOrder,
  workflowTriggerTypes,
} from "../../lib/workflowTrigger";
import { ActivityFacepile } from "../ActivityFacepile";
import { Backdrop } from "../Backdrop";
import {
  BuildStatus,
  getBranchListStatus,
  type StatusFilterValue,
  statusFilterOrder,
} from "../BuildStatus";
import { Button } from "../Button";
import { CircleMesh } from "../CircleMesh";
import { parseBadgeConfig } from "../CircleMeshEditor";
import { ConnectorAvatars } from "../ConnectorAvatars";
import { DismissibleHeroLayout } from "../DismissibleHeroLayout";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import { TriggerTypes } from "../TriggerTypes";
import {
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
} from "../ListFilter";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { countPageCards, PagesList } from "../PagesList";
import { PagesTabHeader } from "../PagesTabHeader";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { WorkflowActionsMenu } from "../WorkflowActionsMenu";
import { workflowListFilters } from "../workflowListFilters";
import { WorkflowNameCell } from "../WorkflowNameCell";
import { CreateWorkflow } from "./CreateWorkflow";
import { SpaceLeaderboard } from "./SpaceLeaderboard";

// Minimum gap above and below the form within the hero; doubled in the height
// math because the form is vertically centered, so the total splits top/bottom.
const HERO_MIN_GAP = 50;

const CleanupModalContent = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 360px;
`;

const CleanupModalTitleBlock = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CleanupModalBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CleanupWorkflowList = styled("div")`
  margin: 0;
  padding: 4px;
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  background: var(--surface-secondary);
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CleanupWorkflowLink = styled("a")`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  & > span:not(.arrow-icon) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  &:hover {
    background: var(--alpha4);
  }

  & .arrow-icon {
    opacity: 0;
    flex-shrink: 0;
    display: flex;
  }

  &:hover .arrow-icon {
    opacity: 0.5;
  }
`;

const CleanupWorkflowBadge = styled("div")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
`;

type Props = {
  spaceId: string;
};

type SpaceDetailData = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadSpace>>
>;
type SpaceDetailWorkflow = SpaceDetailData["workflows"][number];
type WorkflowCreator = NonNullable<SpaceDetailWorkflow["createdBy"]>;
type ConnectorInfo = QueryRowType<
  ReturnType<typeof queries.connectedAppsIndex>
>;

function getWorkflowConnectors(
  workflow: Pick<SpaceDetailWorkflow, "branches">
): string[] {
  return mainBranchConnectorIds(workflow.branches);
}

const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;

export function Space({ spaceId }: Props) {
  const z = useZero();
  const isMobile = useIsMobile();
  const activitySince = roundedHoursAgo(24);
  const [allConnectors] = useQuery(queries.connectedAppsIndex());
  const [spaceQuery, spaceResult] = useQuery(
    queries.loadSpace({ spaceId, activitySince })
  );
  const appsQuery = spaceQuery;
  const appsResult = spaceResult;
  const [onPersonalRoute] = useRouteMatch(routes.personal);
  const [isLinksTab] = useRouteMatch(
    onPersonalRoute ? routes.personal.links : routes.space.links
  );
  const [isLeaderboardTab] = useRouteMatch(routes.space.leaderboard);
  const [rbacMembers] = useQuery(
    isLeaderboardTab ? queries.spaceMembers({ spaceId }) : undefined
  );
  const space = spaceQuery;
  const [workflowViews] = useQuery(queries.userWorkflowViews());
  const [userFavorites] = useQuery(queries.userFavoriteWorkflowIds());
  const displayName = useSpaceDisplayName();
  const adminSubject = useAdminViewSubject();
  const adminSpace = useAdminSpaceView();
  const canWriteSpace = useHasAccess("space:write", spaceId);
  const [readableSpaces] = useSpaces({ settingsView: false });
  const isMember = readableSpaces.some(
    (s) => s.id === spaceId && isSpaceMember(s)
  );
  const baseHeroHeight = isMobile ? 160 : 240;
  const [heroHeight, setHeroHeight] = useState(baseHeroHeight);
  const handleFormHeightChange = useCallback(
    (height: number) => setHeroHeight(height + HERO_MIN_GAP * 2),
    []
  );

  const connectorById = new Map(allConnectors.map((a) => [a.id, a]));
  const backingAppByWorkflowId = new Map(
    allConnectors
      .filter((a) => a.backingWorkflowId && !a.fresh)
      .map((a) => [a.backingWorkflowId!, a])
  );
  const presenceMap = new Map(
    workflowViews.map((v) => [v.workflowId, v.lastSeenAt])
  );
  const favoriteWorkflowIds = new Set(userFavorites.map((f) => f.workflowId));
  const { presentByWorkflow: presentUserIdsByWorkflow } = useGetPresence({
    enabled: spaceResult.type === "complete",
  });

  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    onPersonalRoute ? routes.personal : routes.space,
    {
      name: "asc",
      buildStatus: "desc",
      createdAt: "desc",
      createdBy: "asc",
    }
  );
  const {
    status: statusParam,
    app: appParam,
    creator: creatorParam,
    trigger: triggerParam,
  } = useCurrentUrl(routes.space);
  const statusFilter: StatusFilterValue | null = (
    statusFilterOrder as readonly string[]
  ).includes(statusParam)
    ? (statusParam as StatusFilterValue)
    : null;
  const triggerFilters = parseMultiFilter(triggerParam);
  const appFilters = parseMultiFilter(appParam);
  const creatorFilters = parseMultiFilter(creatorParam);
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);

  useEffect(() => {
    if (spaceResult.type !== "complete") return;
    if (!space?.workflows) {
      navigate(routes.home, undefined, { replace: true });
    }
  }, [spaceResult.type, space?.workflows]);

  const spaceWorkflows = space?.workflows ?? [];
  const thirtyDaysAgo = Date.now() - thirtyDaysMs;
  const isStale = (workflow: SpaceDetailWorkflow) =>
    workflow.branches.every((b) => branchLastActivity(b) < thirtyDaysAgo);
  const staleWorkflows = spaceWorkflows.filter(isStale);

  const handleCleanup = () => {
    for (const workflow of staleWorkflows) {
      z.mutate(sharedMutators.deleteWorkflow({ workflowId: workflow.id }));
    }
    setCleanupModalOpen(false);
  };

  const getWorkflowBuildStatus = (workflow: SpaceDetailWorkflow) => {
    const branch = workflow.branches[0];
    if (!branch || branch.name === "main") return null;
    return getBranchListStatus(branch, presenceMap.get(workflow.id));
  };

  const availableStatuses = new Set<StatusFilterValue>();
  const availableAppIds = new Set<string>();
  const availableCreators = new Map<string, WorkflowCreator>();
  const availableTriggerTypes = new Set<TriggerType>();
  for (const workflow of spaceWorkflows) {
    const status = getWorkflowBuildStatus(workflow);
    if (status) availableStatuses.add(status);
    for (const type of workflowTriggerTypes(workflow.branches)) {
      availableTriggerTypes.add(type);
    }
    for (const appId of getWorkflowConnectors(workflow)) {
      availableAppIds.add(appId);
    }
    if (workflow.createdByUserId && workflow.createdBy) {
      availableCreators.set(workflow.createdByUserId, workflow.createdBy);
    }
  }
  const availableStatusOptions = statusFilterOrder.filter((s) =>
    availableStatuses.has(s)
  );
  const availableTriggerOptions = triggerTypeOrder.filter((t) =>
    availableTriggerTypes.has(t)
  );
  const availableApps = [...availableAppIds]
    .map((id) => connectorById.get(id))
    .filter((a): a is ConnectorInfo => a != null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const filteredWorkflows = spaceWorkflows.filter((workflow) => {
    if (statusFilter !== null) {
      if (getWorkflowBuildStatus(workflow) !== statusFilter) return false;
    }
    if (
      triggerFilters.length > 0 &&
      !workflowTriggerTypes(workflow.branches).some((t) =>
        triggerFilters.includes(t)
      )
    )
      return false;
    if (appFilters.length > 0) {
      const workflowAppIds = getWorkflowConnectors(workflow);
      if (!appFilters.some((id) => workflowAppIds.includes(id))) return false;
    }
    if (creatorFilters.length > 0) {
      if (
        !workflow.createdByUserId ||
        !creatorFilters.includes(workflow.createdByUserId)
      )
        return false;
    }
    return true;
  });

  const filters = workflowListFilters({
    status: { options: availableStatusOptions, value: statusFilter },
    trigger: { options: availableTriggerOptions, values: triggerFilters },
    app: { options: availableApps, values: appFilters },
    creator: { options: availableCreatorOptions, values: creatorFilters },
  });

  const sortedWorkflows = [...filteredWorkflows].sort((a, b) => {
    if (!sortColumn) {
      const aTime = a.updatedAt;
      const bTime = b.updatedAt;
      return bTime - aTime;
    }

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "createdAt":
        aVal = a.createdAt;
        bVal = b.createdAt;
        break;
      case "buildStatus": {
        const getStatusVal = (w: SpaceDetailWorkflow) => {
          const branch = w.branches[0];
          if (!branch || branch.name === "main") return 0;
          const status = getBranchListStatus(branch, presenceMap.get(w.id));
          const priority: Record<string, number> = {
            waiting: 3,
            ready: 2,
            building: 1,
            thinking: 1,
          };
          return (
            (priority[status ?? ""] ?? 0) * 1e15 + (branch.lastChatEntryAt ?? 0)
          );
        };
        aVal = getStatusVal(a);
        bVal = getStatusVal(b);
        break;
      }
      case "createdBy":
        aVal = (a.createdBy ? userDisplayName(a.createdBy) : "").toLowerCase();
        bVal = (b.createdBy ? userDisplayName(b.createdBy) : "").toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const displayedWorkflows = sortedWorkflows;

  const hasBuildStatus = displayedWorkflows.some(
    (w) => getWorkflowBuildStatus(w) != null
  );

  if (!space?.workflows) {
    return null;
  }

  const pagesWorkflows = (appsQuery?.workflows ?? []).filter((w) =>
    w.branches.some((b) => (b.headCommit?.routes?.length ?? 0) > 0)
  );
  const pagesRows = pagesWorkflows.map((w) => ({
    workflow: w,
    space,
  }));

  return (
    <>
      <title>{`${displayName(space)} • ${config.title}`}</title>
      <Backdrop permanentNavigation>
        <DismissibleHeroLayout
          key={spaceId}
          hero={
            adminSubject ||
            (adminSpace && !isMember && !canWriteSpace) ? undefined : (
              <CreateWorkflow
                key={spaceId}
                spaceId={spaceId}
                onFormHeightChange={handleFormHeightChange}
                deferModelQueries={spaceResult.type !== "complete"}
              />
            )
          }
          heroHeight={heroHeight}
          tabHeader={
            <PagesTabHeader
              context={
                onPersonalRoute
                  ? { kind: "personal" }
                  : { kind: "space", spaceId }
              }
              workflowsCount={spaceWorkflows.length}
              linksCount={countPageCards(pagesRows)}
            />
          }
        >
          {isLinksTab ? (
            <PagesList
              rows={pagesRows}
              linksRoute={routes.space.links}
              showSpaceColumn={false}
              emptyTitle="No links"
              emptyDescription="Add a link to a step to give it a shareable URL."
              isComplete={appsResult.type === "complete"}
            />
          ) : isLeaderboardTab ? (
            <SpaceLeaderboard
              spaceId={spaceId}
              workflows={spaceWorkflows}
              pagesWorkflows={pagesWorkflows}
              extraMembers={(() => {
                if (!rbacMembers) return [];
                const members: Array<{
                  id: string;
                  name: string | null;
                  email: string;
                }> = [];
                const seen = new Set<string>();
                for (const g of rbacMembers) {
                  if (g.userGroup) {
                    for (const m of g.userGroup.roleGrantsAsScope) {
                      if (m.user && !seen.has(m.user.id)) {
                        seen.add(m.user.id);
                        members.push(m.user);
                      }
                    }
                  }
                  if (g.user && !seen.has(g.user.id)) {
                    seen.add(g.user.id);
                    members.push(g.user);
                  }
                }
                return members;
              })()}
            />
          ) : (
            <ListTable
              columns={{ activity: "fixed-activity" }}
              headers={
                isMobile
                  ? { name: "Workflow" }
                  : {
                      name: "Workflow",
                      buildStatus: hasBuildStatus ? "3B status" : "",
                      apps: "Connectors",
                      trigger: "Trigger",
                      createdAt: "Created",
                      createdBy: "Created by",
                      activity: "",
                      actions: "",
                    }
              }
              sortableColumns={isMobile ? [] : sortableColumns}
              dateColumns={isMobile ? [] : ["createdAt", "buildStatus"]}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              footer={
                hasActiveFilters(filters) ? (
                  <ListFilterPills filters={filters} />
                ) : null
              }
              headerActions={
                <>
                  {staleWorkflows.length > 0 ? (
                    <Tooltip content="Tidy up">
                      <Button
                        variant="secondary"
                        icon="brush-cleaning"
                        onClick={() => setCleanupModalOpen(true)}
                      />
                    </Tooltip>
                  ) : null}
                  <ListFilter filters={filters} />
                </>
              }
              emptyState={
                spaceResult.type === "complete" ? (
                  spaceWorkflows.length > 0 ? (
                    <EmptyState
                      title="No matches"
                      description="Try a different filter."
                      iconName="product-workflows"
                    />
                  ) : (
                    <EmptyState
                      title="No workflows"
                      description="Workflows added to this space appear here."
                      iconName="product-workflows"
                    />
                  )
                ) : null
              }
              items={displayedWorkflows.map((workflow) => {
                const workflowBuildStatus = getWorkflowBuildStatus(workflow);
                return {
                  key: workflow.id,
                  link: makeUrl(routes.space.workflow, {
                    spaceId,
                    workflowId: workflow.id,
                  }),
                  name: (hover) => (
                    <WorkflowNameCell
                      workflow={workflow}
                      hover={hover}
                      connectedAppName={
                        backingAppByWorkflowId.get(workflow.id)?.name
                      }
                    />
                  ),
                  buildStatus: <BuildStatus status={workflowBuildStatus} />,
                  trigger: <TriggerTypes branches={workflow.branches} />,
                  apps: (
                    <ConnectorAvatars
                      connected={connectorAvatarItems(
                        getWorkflowConnectors(workflow),
                        connectorById
                      )}
                    />
                  ),
                  activity: (
                    <ActivityFacepile
                      contributors={workflow.contributors}
                      presentUserIds={presentUserIdsByWorkflow.get(workflow.id)}
                    />
                  ),
                  createdAt: (
                    <Text variant="text-small">
                      {new Date(workflow.createdAt).toLocaleDateString()}
                    </Text>
                  ),
                  createdBy: (
                    <Text
                      variant="text-small"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {workflow.createdBy
                        ? userDisplayName(workflow.createdBy)
                        : null}
                    </Text>
                  ),
                  actions: (hover) => {
                    if (!hover) return null;
                    const isFavorite = favoriteWorkflowIds.has(workflow.id);
                    return (
                      <div style={{ marginLeft: "auto" }}>
                        <WorkflowActionsMenu
                          workflowId={workflow.id}
                          spaceId={spaceId}
                          isFavorite={isFavorite}
                          autofixEnabled={workflow.autofixEnabled ?? true}
                          autoImproveEnabled={
                            workflow.autoImproveEnabled ?? false
                          }
                        />
                      </div>
                    );
                  },
                };
              })}
            />
          )}
        </DismissibleHeroLayout>
      </Backdrop>
      <Modal open={cleanupModalOpen} onClose={() => setCleanupModalOpen(false)}>
        <CleanupModalContent>
          {staleWorkflows.length > 0 ? (
            <>
              <CleanupModalTitleBlock>
                <Text variant="text-regular-md" color="var(--text-primary)">
                  Tidy up
                </Text>
                <Text variant="text-regular" color="var(--text-secondary)">
                  You have {staleWorkflows.length}{" "}
                  {staleWorkflows.length === 1 ? "workflow" : "workflows"} with
                  no chats or runs in the last 30 days. Remove{" "}
                  {staleWorkflows.length === 1 ? "it" : "them"} to keep things
                  tidy.
                </Text>
              </CleanupModalTitleBlock>
              <CleanupModalBody>
                <CleanupWorkflowList>
                  {staleWorkflows.map((workflow) => {
                    const badgeConfig = parseBadgeConfig(
                      workflow.badgeSeed,
                      workflow.id
                    );
                    return (
                      <CleanupWorkflowLink
                        key={workflow.id}
                        href={makeUrl(routes.space.workflow, {
                          spaceId,
                          workflowId: workflow.id,
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <CleanupWorkflowBadge>
                          <CircleMesh
                            key={workflow.badgeSeed}
                            targetCircleSize={8}
                            seed={badgeConfig.seed}
                            mutingIntensity={0}
                            animate={false}
                            restrictedColors={badgeConfig.colors}
                            grayscale={workflow.pausedAt != null}
                          />
                        </CleanupWorkflowBadge>
                        <Text
                          variant="text-regular"
                          color="var(--text-primary)"
                        >
                          {workflow.name}
                        </Text>
                        <span className="arrow-icon">
                          <Icon name="arrow-up-right" size={12} />
                        </span>
                      </CleanupWorkflowLink>
                    );
                  })}
                </CleanupWorkflowList>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button size="l" variant="danger" onClick={handleCleanup}>
                    {staleWorkflows.length === 1 ? "Delete" : "Delete all"}
                  </Button>
                </div>
              </CleanupModalBody>
            </>
          ) : (
            <CleanupModalTitleBlock>
              <Text variant="text-regular-md" color="var(--text-primary)">
                Tidy up
              </Text>
              <Text variant="text-regular" color="var(--text-secondary)">
                Nothing to tidy — all your workflows have been active in the
                last 30 days. You{"'"}re already on top of things.
              </Text>
            </CleanupModalTitleBlock>
          )}
        </CleanupModalContent>
      </Modal>
    </>
  );
}
